import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { ApiError, authApi } from '../api';
import { AuthShell } from '../components/AuthShell';

const COUNTRIES: ReadonlyArray<{ code: string; name: string }> = [
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'QA', name: 'Qatar' },
  { code: 'KW', name: 'Kuwait' },
  { code: 'BH', name: 'Bahrain' },
  { code: 'OM', name: 'Oman' },
  { code: 'IN', name: 'India' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'US', name: 'United States' },
];

const signupSchema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/\d/, 'Password must include a number'),
  full_name: z.string().min(1, 'Full name is required'),
  org_name: z.string().min(1, 'Organization name is required'),
  country: z.string().length(2, 'Country must be a 2-letter code').optional().or(z.literal('')),
  accept_terms: z.literal(true, {
    errorMap: () => ({ message: 'You must accept the terms to continue' }),
  }),
});

type SignupValues = z.infer<typeof signupSchema>;

export function SignupPage() {
  const navigate = useNavigate();
  const [formError, setFormError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SignupValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      email: '',
      password: '',
      full_name: '',
      org_name: '',
      country: 'AE',
    } as SignupValues,
  });

  const onSubmit = async (values: SignupValues) => {
    setFormError(null);
    try {
      const res = await authApi.signup({
        email: values.email,
        password: values.password,
        full_name: values.full_name,
        org_name: values.org_name,
        country: values.country || undefined,
        accept_terms: values.accept_terms,
      });
      navigate(`/verify-email?email=${encodeURIComponent(values.email)}&tenant=${res.tenant_id}`);
    } catch (err) {
      if (err instanceof ApiError) {
        setFormError(err.problem.detail || err.problem.title || 'Signup failed');
      } else {
        setFormError('Unexpected error — please try again');
      }
    }
  };

  return (
    <AuthShell mode="signup">
      <h1 className="text-2xl font-semibold tracking-tight">Start your free trial</h1>
      <p className="mt-1.5 text-sm text-muted-foreground">No credit card required.</p>

      <form className="mt-8 space-y-5" onSubmit={handleSubmit(onSubmit)} noValidate>
        <Field label="Work email" error={errors.email?.message}>
          <input type="email" autoComplete="email" className="auth-input" {...register('email')} />
        </Field>
        <Field label="Password" hint="Min 8 characters, 1 number" error={errors.password?.message}>
          <input type="password" className="auth-input" {...register('password')} />
        </Field>
        <Field label="Your full name" error={errors.full_name?.message}>
          <input type="text" className="auth-input" {...register('full_name')} />
        </Field>
        <Field
          label="Organization name"
          hint='e.g., "Dynamic Leo Transport"'
          error={errors.org_name?.message}
        >
          <input type="text" className="auth-input" {...register('org_name')} />
        </Field>
        <Field label="Country" error={errors.country?.message}>
          <select className="auth-input" {...register('country')}>
            {COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.name} ({c.code})
              </option>
            ))}
          </select>
        </Field>

        {/* Legal links open in a new tab so a half-filled form isn't lost. */}
        <div>
          <label className="flex items-start gap-2.5 text-sm">
            <input
              type="checkbox"
              className="auth-checkbox mt-0.5"
              aria-describedby="terms-error"
              {...register('accept_terms')}
            />
            <span>
              I agree to the{' '}
              <Link
                to="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-primary underline underline-offset-2 hover:no-underline"
              >
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link
                to="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-primary underline underline-offset-2 hover:no-underline"
              >
                Privacy Policy
              </Link>
              .
            </span>
          </label>
          {errors.accept_terms && (
            <p id="terms-error" className="mt-1.5 text-xs text-danger">
              {errors.accept_terms.message}
            </p>
          )}
          <p className="mt-2 text-xs text-muted-foreground">
            Read them any time at{' '}
            <Link
              to="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              /terms
            </Link>{' '}
            and{' '}
            <Link
              to="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              /privacy
            </Link>
            .
          </p>
        </div>

        {formError && (
          <p className="rounded bg-danger/10 px-3 py-2 text-xs text-danger" role="alert">
            {formError}
          </p>
        )}

        <button type="submit" disabled={isSubmitting} className="auth-btn">
          {isSubmitting ? 'Creating workspace…' : 'Create my workspace'}
        </button>
      </form>

      <p className="mt-6 text-center text-sm text-muted-foreground">
        Already have an account?{' '}
        <Link to="/login" className="font-medium text-primary hover:underline">
          Log in
        </Link>
      </p>
    </AuthShell>
  );
}

function Field({
  label,
  error,
  hint,
  children,
}: {
  label: string;
  error?: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-sm font-medium">{label}</label>
      <div className="mt-1.5">{children}</div>
      {hint && <p className="mt-1.5 text-xs text-muted-foreground">{hint}</p>}
      {error && <p className="mt-1.5 text-xs text-danger">{error}</p>}
    </div>
  );
}
